import { useState } from "react";
import { Check, ChevronsUpDown, Search, User, Mail, Briefcase, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAccountContacts, AccountContact } from "@/hooks/useAccountContacts";
import { cn } from "@/lib/utils";

interface AccountContactPickerProps {
  accountId: string | null | undefined;
  onSelect: (contact: AccountContact) => void;
  onManualAdd?: () => void;
  placeholder?: string;
  disabled?: boolean;
}

export function AccountContactPicker({
  accountId,
  onSelect,
  onManualAdd,
  placeholder = "Search contacts...",
  disabled = false,
}: AccountContactPickerProps) {
  const [open, setOpen] = useState(false);
  const { data: contacts = [], isLoading } = useAccountContacts(accountId);

  const noAccount = !accountId;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || noAccount}
          className="gap-1.5"
        >
          <Search className="h-3.5 w-3.5" />
          {noAccount ? "Link account first" : `Link Contact (${contacts.length})`}
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>
              {isLoading ? "Loading contacts..." : "No contacts found for this account"}
            </CommandEmpty>
            <CommandGroup heading={`Account Contacts (${contacts.length})`}>
              {contacts.map((contact) => (
                <CommandItem
                  key={contact.id}
                  value={`${contact.full_name || ""} ${contact.email || ""} ${contact.job_title || ""}`}
                  onSelect={() => {
                    onSelect(contact);
                    setOpen(false);
                  }}
                  className="flex flex-col items-start gap-0.5 py-2"
                >
                  <div className="flex items-center gap-2 w-full">
                    <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="font-medium text-sm truncate">
                      {contact.full_name || "Unnamed"}
                    </span>
                    {contact.seniority_level && (
                      <span className="ml-auto text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                        {contact.seniority_level}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 pl-5 text-xs text-muted-foreground">
                    {contact.job_title && (
                      <span className="flex items-center gap-1 truncate">
                        <Briefcase className="h-3 w-3 shrink-0" />
                        {contact.job_title}
                      </span>
                    )}
                    {contact.email && (
                      <span className="flex items-center gap-1 truncate">
                        <Mail className="h-3 w-3 shrink-0" />
                        {contact.email}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            {onManualAdd && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      onManualAdd();
                      setOpen(false);
                    }}
                    className="text-muted-foreground"
                  >
                    <UserPlus className="h-3.5 w-3.5 mr-2" />
                    Add contact manually
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
